import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { StaffService } from '../../staff/staff.service';
import { CommunicationService } from '../communication.service';

interface ChatThread {
  id: number;
  type: 'direct' | 'group';
  name: string;
  members: string[];
  lastMessage: string;
  unread: number;
}

interface ChatMessage {
  id: number;
  sender: string;
  text: string;
  time: string;
  pending?: boolean;
}

@Component({
  selector: 'app-communication-chats',
  templateUrl: './communication-chats.component.html',
  styleUrls: ['./communication-chats.component.scss']
})
export class CommunicationChatsComponent implements OnInit, OnDestroy {
  loadingThreads = false;
  loadingUsers = false;
  userSearchTerm = '';
  groupMemberSearchTerm = '';
  selectedDirectUser: any = null;
  selectedGroupMembers: any[] = [];
  groupError = '';
  staffOptions: any[] = [];
  threads: ChatThread[] = [];
  selectedThread: ChatThread | null = null;
  newGroupName = '';
  draftMessage = '';
  conversations: Record<number, ChatMessage[]> = {};
  private messageSub?: Subscription;

  constructor(
    private staffService: StaffService,
    private communication: CommunicationService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.loadStaffUsers();
    this.loadThreads();
    this.connectRealtime();
  }

  async ngOnDestroy(): Promise<void> {
    for (const thread of this.threads) {
      await this.communication.leaveThread(thread.id).catch(() => {});
    }
    this.messageSub?.unsubscribe();
  }

  get directThreads(): ChatThread[] {
    return this.threads.filter(thread => thread.type === 'direct');
  }

  get groupThreads(): ChatThread[] {
    return this.threads.filter(thread => thread.type === 'group');
  }

  get directUnreadCount(): number {
    return this.directThreads.reduce((total, thread) => total + Number(thread.unread || 0), 0);
  }

  get groupUnreadCount(): number {
    return this.groupThreads.reduce((total, thread) => total + Number(thread.unread || 0), 0);
  }

  get activeConversation(): ChatMessage[] {
    return this.selectedThread ? (this.conversations[this.selectedThread.id] || []) : [];
  }

  get filteredUsers(): any[] {
    return this.filterUsers(this.userSearchTerm);
  }

  get filteredGroupMemberUsers(): any[] {
    return this.filterUsers(this.groupMemberSearchTerm);
  }

  get canCreateGroup(): boolean {
    return !!this.newGroupName.trim() && this.selectedGroupMembers.length >= 3;
  }

  createGroup(): void {
    const name = this.newGroupName.trim();
    if (!name) {
      this.groupError = 'Enter a group name.';
      return;
    }

    if (this.selectedGroupMembers.length < 3) {
      this.groupError = 'Select at least 3 users to create a group.';
      return;
    }

    const staffIds = this.selectedGroupMembers.map(user => Number(user.id || user.staffId)).filter(Boolean);
    this.communication.createGroupThread(name, staffIds).subscribe({
      next: (thread) => {
        const mapped = this.mapThread(thread);
        this.upsertThread(mapped);
        this.selectThread(mapped);
        this.newGroupName = '';
        this.selectedGroupMembers = [];
        this.groupMemberSearchTerm = '';
        this.groupError = '';
      },
      error: (err) => {
        this.groupError = err?.error?.message || 'Failed to create group.';
      }
    });
  }

  startDirectChat(user: any): void {
    if (!user) return;

    const staffId = Number(user.id || user.staffId);
    if (!staffId) return;

    this.communication.createDirectThread(staffId).subscribe({
      next: (thread) => {
        const mapped = this.mapThread(thread);
        this.upsertThread(mapped);
        this.selectThread(mapped);
        this.userSearchTerm = '';
        this.selectedDirectUser = null;
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to start direct chat.', 'error')
    });
  }

  onDirectUserSelected(user: any): void {
    this.startDirectChat(user);
  }

  async selectThread(thread: ChatThread): Promise<void> {
    this.selectedThread = thread;
    thread.unread = 0;
    await this.communication.joinThread(thread.id).catch(() => {});

    this.communication.getMessages(thread.id).subscribe({
      next: (messages) => {
        this.conversations[thread.id] = (messages || []).map(message => this.mapMessage(message));
        this.communication.markThreadRead(thread.id).subscribe({ next: () => {}, error: () => {} });
      },
      error: () => {
        this.conversations[thread.id] = [];
      }
    });
  }

  sendChat(): void {
    const text = this.draftMessage.trim();
    if (!text || !this.selectedThread) return;

    const threadId = this.selectedThread.id;
    const pendingMessage: ChatMessage = {
      id: -Date.now(),
      sender: 'You',
      text,
      time: this.nowLabel(),
      pending: true
    };
    this.conversations[threadId] = [...this.activeConversation, pendingMessage];
    this.selectedThread.lastMessage = text;
    this.draftMessage = '';

    this.communication.sendThreadMessage(threadId, text).catch(() => {
      this.communication.sendMessage(threadId, text).subscribe({
        next: (message) => this.appendMessage(message),
        error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to send chat message.', 'error')
      });
    });
  }

  private loadThreads(): void {
    this.loadingThreads = true;
    this.communication.getThreads().subscribe({
      next: (threads) => {
        this.threads = (threads || []).map(thread => this.mapThread(thread));
        this.loadingThreads = false;
        this.threads.forEach(thread => this.communication.joinThread(thread.id).catch(() => {}));
        if (!this.selectedThread && this.threads.length) {
          this.selectThread(this.threads[0]);
        }
      },
      error: (err) => {
        this.loadingThreads = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load chats.', 'error');
      }
    });
  }

  private loadStaffUsers(): void {
    this.loadingUsers = true;
    this.staffService.getAll({ isActive: true }).subscribe({
      next: (users) => {
        this.staffOptions = users || [];
        this.loadingUsers = false;
      },
      error: () => {
        this.staffOptions = [];
        this.loadingUsers = false;
      }
    });
  }

  private connectRealtime(): void {
    this.communication.connectHub().catch(() => {});
    this.messageSub = this.communication.messages$.subscribe(message => this.appendMessage(message));
  }

  private appendMessage(message: any): void {
    const threadId = Number(message.threadId || message.communicationThreadId);
    if (!threadId) return;

    const mapped = this.mapMessage(message);
    const existing = this.conversations[threadId] || [];
    const pendingIndex = existing.findIndex(item => item.pending && item.sender === mapped.sender && item.text === mapped.text);
    if (pendingIndex >= 0) {
      const updated = [...existing];
      updated[pendingIndex] = mapped;
      this.conversations[threadId] = updated;
    } else if (!existing.some(item => item.id === mapped.id)) {
      this.conversations[threadId] = [...existing, mapped];
    }

    const thread = this.threads.find(item => item.id === threadId);
    if (thread) {
      thread.lastMessage = mapped.text;
      if (this.selectedThread?.id !== threadId && mapped.sender !== 'You') {
        thread.unread += 1;
      }
      this.upsertThread(thread);
    }
  }

  private upsertThread(thread: ChatThread): void {
    const others = this.threads.filter(item => item.id !== thread.id);
    this.threads = [thread, ...others];
  }

  private mapThread(thread: any): ChatThread {
    const participants = thread.participants || [];
    return {
      id: Number(thread.id),
      type: String(thread.threadType || '').toLowerCase() === 'direct' ? 'direct' : 'group',
      name: thread.title || 'Chat',
      members: participants.map((p: any) => p.staffName || p.email || 'Staff User'),
      lastMessage: thread.lastMessage?.message || 'No messages yet.',
      unread: Number(thread.unreadCount || 0)
    };
  }

  private mapMessage(message: any): ChatMessage {
    const currentName = this.auth.getUser()?.fullName || 'You';
    const sender = message.senderName || 'System';
    return {
      id: Number(message.id || 0),
      sender: sender === currentName ? 'You' : sender,
      text: message.message || message.text || '',
      time: message.createdAt ? this.formatWhen(message.createdAt) : this.nowLabel()
    };
  }

  private filterUsers(term: string): any[] {
    const q = term.trim().toLowerCase();
    const users = this.staffOptions || [];
    if (!q) return users;

    return users.filter(user => {
      const hay = [
        user.fullName,
        user.email,
        user.department,
        user.position
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }

  private formatWhen(value: string): string {
    return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private nowLabel(): string {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
